import { useCallback, useEffect, useMemo, useState } from "react";

import {
  groupSkillFrontmatterEntries,
  parseSkillFrontmatterEntries,
  stringifySkillFrontmatterEntries,
} from "../../../utils/skill-frontmatter";
import { type FrontmatterFormState, buildFormState } from "../utils";

interface UseFrontmatterEditStateParams {
  frontmatter: string | null;
  onChange?: (frontmatter: string | null) => void;
}

export const useFrontmatterEditState = ({
  frontmatter,
  onChange,
}: UseFrontmatterEditStateParams) => {
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [accordionValue, setAccordionValue] = useState<string | undefined>("frontmatter");
  const entries = useMemo(() => parseSkillFrontmatterEntries(frontmatter), [frontmatter]);
  const [draftState, setDraftState] = useState<FrontmatterFormState>(() => buildFormState(entries));
  const grouped = useMemo(() => groupSkillFrontmatterEntries(entries), [entries]);

  useEffect(() => {
    setDraftState(buildFormState(entries));
  }, [entries, frontmatter]);

  const applyDraftState = useCallback(() => {
    if (!onChange) {
      return;
    }

    const nextEntries = [
      { key: "name", value: draftState.name },
      { key: "description", value: draftState.description },
      { key: "license", value: draftState.license },
      { key: "compatibility", value: draftState.compatibility },
      { key: "allowed-tools", value: draftState.allowedTools },
      {
        key: "disable-model-invocation",
        value: draftState.manualOnlyInvocation ? "true" : "",
      },
      ...draftState.extraEntries,
    ];

    onChange(stringifySkillFrontmatterEntries(nextEntries));
    setIsEditingMetadata(false);
  }, [draftState, onChange]);

  const cancelEditing = useCallback(() => {
    setDraftState(buildFormState(entries));
    setIsEditingMetadata(false);
  }, [entries]);

  return {
    isEditingMetadata,
    setIsEditingMetadata,
    accordionValue,
    setAccordionValue,
    entries,
    draftState,
    setDraftState,
    grouped,
    applyDraftState,
    cancelEditing,
  };
};
